/**
 * Utilities for handling product options and configurations
 * Supports the hierarchical Options JSON structure
 */
import { getLanguageFromUrl } from './languageUtils';

/**
 * Generates product combinations from the Options structure
 * @param {Object} supportedModels - The SupportedModels object from config
 * @returns {Array} Array of product objects
 */
export const generateProductCombinations = (supportedModels) => {
  if (!supportedModels || !supportedModels.Options) {
    console.warn('Invalid SupportedModels format');
    return [];
  }

  const { ProductName, Options, CommonProperties } = supportedModels;
  const products = [];

  // Build ordered option keys (Option1..OptionN first if present, else any keys)
  const entries = Object.entries(Options)
    .filter(([, def]) => def && Array.isArray(def.values) && def.values.length > 0)
    .sort(([a], [b]) => {
      const na = parseInt(String(a).replace(/[^0-9]/g, ''), 10) || 0;
      const nb = parseInt(String(b).replace(/[^0-9]/g, ''), 10) || 0;
      return na - nb;
    });

  if (entries.length === 0) return [];

  // Recursive combination builder supporting any number of options
  const optionKeys = entries.map(([k]) => k);

  const backtrack = (idx, picked) => {
    if (idx >= optionKeys.length) {
      // Build product object from picked selections
      const valuesPath = picked.map(p => p.value).filter(Boolean);
      const productionName = [ProductName, ...valuesPath].filter(Boolean).join('/');
      products.push(createProductObjectDynamic(productionName, picked, CommonProperties, Options));
      return;
    }

    const key = optionKeys[idx];
    const values = Options[key]?.values || [];
    if (!values.length) {
      backtrack(idx + 1, picked);
      return;
    }
    for (const val of values) {
      backtrack(idx + 1, [...picked, val]);
    }
  };

  backtrack(0, []);
  return products;
};

/**
 * Creates a product object from option values
 * Backward compatible wrapper that accepts up to 3 picked option values.
 * Prefer using createProductObjectDynamic for arbitrary counts.
 * @param {String} productionName - The production name (path)
 * @param {Object} opt1 - First option value (legacy: Option1)
 * @param {Object} opt2 - Second option value (legacy: Option2)
 * @param {Object} opt3 - Third option value (legacy: Option3)
 * @param {Object} commonProps - Common properties for all products
 * @param {Object} options - The Options object with metadata
 * @returns {Object} Product object
 */
const createProductObject = (productionName, opt1, opt2, opt3, commonProps, options) => {
  // Backward-compatible wrapper calling dynamic creator
  const picked = [opt1, opt2, opt3].filter(Boolean);
  return createProductObjectDynamic(productionName, picked, commonProps, options);
};

// New dynamic creator that supports any number of picked options
const createProductObjectDynamic = (productionName, pickedValues, commonProps, options) => {
  const product = {
    ProductionName: productionName,
    DisplayName: (pickedValues[0] && getOptionDisplayName(pickedValues[0].displayName)) || '',
    Views: commonProps?.Views || [],
    Options: {}
  };

  // Fill Options in Option1..OptionN order when possible
  const optionEntries = Object.entries(options || {}).sort(([a],[b]) => {
    const na = parseInt(String(a).replace(/[^0-9]/g, ''), 10) || 0;
    const nb = parseInt(String(b).replace(/[^0-9]/g, ''), 10) || 0;
    return na - nb;
  });
  for (let i = 0; i < optionEntries.length && i < pickedValues.length; i++) {
    const key = optionEntries[i][0];
    product.Options[key] = pickedValues[i]?.value ?? pickedValues[i];
  }

  if (commonProps && commonProps['3DMODEL']) {
    product['3DMODEL'] = commonProps['3DMODEL'];
  }

  // Attempt to set colors if any option represents Color
  try {
    const colorKey = optionEntries.find(([, def]) => def?.name === 'Color' || getOptionDisplayName(def?.displayName) === 'Card Color')?.[0];
    if (colorKey) {
      const idx = optionEntries.findIndex(([k]) => k === colorKey);
      const val = pickedValues[idx]?.value;
      if (val) product.colors = [val];
    }
  } catch {}

  return product;
};

/**
 * Resolve an option meta by key (e.g., 'Option1') or by its name/displayName
 * @param {Object} optionsCfg
 * @param {string} keyOrName
 * @returns {Object|null}
 */
export const resolveOptionMeta = (optionsCfg, keyOrName) => {
  if (!optionsCfg || !keyOrName) return null;
  const direct = optionsCfg[keyOrName];
  if (direct) return direct;
  const entries = Object.entries(optionsCfg);
  const lower = String(keyOrName).toLowerCase();
  for (const [k, meta] of entries) {
    const n = meta?.name ? String(meta.name).toLowerCase() : '';
    const dnEn = meta?.displayName?.en ? String(meta.displayName.en).toLowerCase() : '';
    const dnCn = meta?.displayName?.cn ? String(meta.displayName.cn).toLowerCase() : '';
    if (lower === n || lower === dnEn || lower === dnCn) return meta;
  }
  return null;
};

/**
 * Resolve an option key (e.g., 'Option1') by its human name
 * @param {Object} optionsCfg
 * @param {string} name
 * @returns {string|undefined}
 */
export const resolveOptionKeyByName = (optionsCfg, name) => {
  if (!optionsCfg || !name) return undefined;
  const lower = String(name).toLowerCase();
  return Object.keys(optionsCfg).find(k => {
    const meta = optionsCfg[k];
    const n = meta?.name ? String(meta.name).toLowerCase() : '';
    const dnEn = meta?.displayName?.en ? String(meta.displayName.en).toLowerCase() : '';
    const dnCn = meta?.displayName?.cn ? String(meta.displayName.cn).toLowerCase() : '';
    return lower === n || lower === dnEn || lower === dnCn;
  });
};

/**
 * Gets the display name for an option value with translation support
 * @param {Object} displayName - The display name object with translations
 * @returns {String} Translated display name
 */
export const getOptionDisplayName = (displayName) => {
  if (!displayName) return '';
  
  if (typeof displayName === 'string') {
    return displayName; // Backward compatibility
  }
  
  if (typeof displayName === 'object') {
    const currentLang = getLanguageFromUrl();
    return displayName[currentLang] || displayName['en'] || displayName['cn'] || '';
  }
  
  return '';
};

/**
 * Builds a product path from options (legacy helper for up to 3 segments)
 * Prefer buildProductPathFromSelections for dynamic options.
 * @param {String} productName - Base product name
 * @param {String} option1 - First option value
 * @param {String} option2 - Second option value
 * @param {String} option3 - Third option value
 * @returns {String} Product path
 */
export const buildProductPath = (productName, option1, option2, option3) => {
  // Backward-compatible: build from provided args
  const parts = [productName];
  if (option1) parts.push(option1);
  if (option2) parts.push(option2);
  if (option3) parts.push(option3);
  return parts.join('/');
};

/**
 * Build product path from dynamic selections and Options config.
 * Sorts options by numeric suffix to maintain order.
 * @param {String} productName
 * @param {Object} optionsCfg - SupportedModels.Options
 * @param {Object} selectedOptions - map of optionKeyOrName => value or { value }
 * @returns {String}
 */
export const buildProductPathFromSelections = (productName, optionsCfg, selectedOptions) => {
  const parts = [productName];
  try {
    const entries = Object.entries(optionsCfg || {}).sort(([a],[b]) => {
      const na = parseInt(String(a).replace(/[^0-9]/g, ''), 10) || 0;
      const nb = parseInt(String(b).replace(/[^0-9]/g, ''), 10) || 0;
      return na - nb;
    });
    for (const [key] of entries) {
      // Accept selection by key or by meta.name
      const byKey = selectedOptions?.[key];
      let val = byKey?.value ?? byKey;
      if (!val) {
        const name = optionsCfg?.[key]?.name;
        const byName = name ? selectedOptions?.[name] : undefined;
        val = byName?.value ?? byName;
      }
      if (val) parts.push(String(val));
    }
  } catch {}
  return parts.join('/');
};

/**
 * Parses a product path into components (legacy helper for up to 3 option segments)
 * @param {String} path - Product path
 * @returns {Object} Parsed components
 */
export const parseProductPath = (path) => {
  if (!path) return {};
  
  const parts = path.split('/');
  
  return {
    productName: parts[0] || '',
    option1: parts[1] || '',
    option2: parts[2] || '',
    option3: parts[3] || '',
  };
};

/**
 * Gets a product by its path
 * @param {Array} products - Array of products
 * @param {String} path - Product path
 * @returns {Object|null} Found product or null
 */
export const getProductByPath = (products, path) => {
  if (!products || !Array.isArray(products) || !path) {
    return null;
  }
  
  return products.find(p => p.ProductionName === path) || null;
};

/**
 * Gets option values for a specific option by key or by its name
 * @param {Object} options - Options object
 * @param {String} keyOrName - Option key (e.g., 'Option1') or option name
 * @returns {Array} Option values
 */
export const getOptionValues = (options, keyOrName) => {
  if (!options || !keyOrName) return [];
  const meta = resolveOptionMeta(options, keyOrName);
  const vals = meta?.values;
  return Array.isArray(vals) ? vals : [];
};

/**
 * Gets the display/human name of an option by key or name
 * @param {Object} options - Options object
 * @param {String} keyOrName - Option key or name
 * @returns {String} Option display name
 */
export const getOptionName = (options, keyOrName) => {
  if (!options || !keyOrName) return '';
  const meta = resolveOptionMeta(options, keyOrName);
  if (!meta) return '';
  return getOptionDisplayName(meta.displayName) || meta.name || '';
};

/**
 * Helper: normalize a view (string or object) to its id
 * @param {string|Object} view
 * @returns {string}
 */
export const getViewIdFromView = (view) => {
  if (!view) return '';
  if (typeof view === 'string') return view;
  if (typeof view === 'object' && view.id) return view.id;
  return String(view);
};

/**
 * Helper: determine if an option key is a view-affecting option
 * Accepts either isViewOption or isVisualOption flags for backward compatibility.
 * @param {Object} optionsCfg
 * @param {string} key
 */
export const isViewOptionKey = (optionsCfg, key) => {
  try {
    const meta = optionsCfg?.[key];
    if (!meta) return false;
    return meta.isViewOption === true || meta.isVisualOption === true;
  } catch {
    return false;
  }
};

/**
 * Compute a map of viewId => enabled(boolean) from selected options using linked_view.
 * Later options take precedence when conflicts occur.
 * If no mapping is produced, returns an empty map (meaning no filtering).
 * @param {Object} optionsCfg SupportedModels.Options
 * @param {Object} selectedOptions Global selected options (values may be plain or {value})
 * @returns {Map<string, boolean>} map of viewId => enabled
 */
export const computeLinkedViewMap = (optionsCfg, selectedOptions) => {
  const result = new Map();
  try {
    if (!optionsCfg || !selectedOptions) return result;
    const keys = Object.keys(selectedOptions);
    for (const key of keys) {
      // Resolve option meta by either the direct key (OptionX) or by matching its name field
      let optMeta = optionsCfg[key];
      if (!optMeta) {
        const matchedKey = Object.keys(optionsCfg).find(k => optionsCfg[k]?.name === key);
        if (matchedKey) optMeta = optionsCfg[matchedKey];
      }
      if (!optMeta) continue;
      if (!(optMeta.isViewOption === true || optMeta.isVisualOption === true)) continue;

      const selectedVal = selectedOptions[key]?.value ?? selectedOptions[key];
      const values = Array.isArray(optMeta?.values) ? optMeta.values : [];
      const found = values.find(v => {
        const vv = v?.value ?? v;
        if (vv === selectedVal) return true;
        const en = v?.displayName?.en;
        const cn = v?.displayName?.cn;
        return selectedVal === en || selectedVal === cn;
      });
      const linked = found?.linked_view;
      if (linked && typeof linked === 'object') {
        Object.keys(linked).forEach(viewId => {
          const enabled = Boolean(linked[viewId]);
          result.set(viewId, enabled);
        });
      }
    }
  } catch {}
  return result;
};

/**
 * Filter the provided views array using linked_view rules derived from options.
 * If the computed map is empty, return original views.
 * When any viewId is explicitly mapped to false, it will be removed from results.
 * If mapped to true, it will be kept (does not force-add if not present).
 * @param {Object} optionsCfg SupportedModels.Options
 * @param {Object} selectedOptions Global selected options
 * @param {Array<string|Object>} views The source views array
 * @returns {Array<string|Object>} filtered views
 */
export const filterViewsByLinkedView = (optionsCfg, selectedOptions, views) => {
  const source = Array.isArray(views) ? views : [];
  const map = computeLinkedViewMap(optionsCfg, selectedOptions);
  if (map.size === 0) return source;
  return source.filter(v => {
    const id = getViewIdFromView(v);
    if (!id) return false;
    if (map.has(id)) {
      return map.get(id) === true;
    }
    // If not specified, keep by default
    return true;
  });
};

/**
 * Build a Set of disabled viewIds based on linked_view mapping for quick checks.
 * @param {Object} optionsCfg
 * @param {Object} selectedOptions
 * @returns {Set<string>}
 */
export const getDisabledViewsSet = (optionsCfg, selectedOptions) => {
  const set = new Set();
  const map = computeLinkedViewMap(optionsCfg, selectedOptions);
  map.forEach((enabled, viewId) => {
    if (enabled === false) set.add(viewId);
  });
  return set;
};
